import {
    LendingVaultExpanded,
    useActiveWallet,
    useOraclePrices,
    UserVaultPnl,
    useUserVaultPnlFromDepositSummary,
    UserVaultPositionExpanded,
    isRecentVaultDepositOrWithdrawal
} from "@bridgesplit/abf-react";
import { Column, OutlinedCard, Row, StatRow, Text } from "@bridgesplit/ui";
import { StackedCurrencyDisplay } from "app/components/common";
import { formatUsd } from "@bridgesplit/utils";

import { useVaultContext } from "./VaultContext";
import { ProfitLossText } from "../common/util";
import { COPY } from "../../constants";
import { VaultPendingPnl } from "./position";

interface VaultDepositStats {
    totalDepositsPrincipal: number | undefined;
    totalDepositsUsd: number | undefined;
}

export function VaultDepositCard() {
    const { activeWallet } = useActiveWallet();
    const {
        vaultExpanded,
        vaultDepositSummary,
        userPositionLoading,
        userPosition,
        userPositionFetching,
        vaultDepositSummaryLoading,
        vaultDepositSummaryFetching
    } = useVaultContext();
    const userVaultPnl = useUserVaultPnlFromDepositSummary(vaultDepositSummary, vaultExpanded, userPosition);

    const { getUsdPrice } = useOraclePrices([vaultExpanded?.vault.principalMint]);

    const borrowStats = useUserVaultDepositStats();
    const principalPrice = getUsdPrice(vaultExpanded?.vault.principalMint);
    const pnlStats = getVaultPnLStats(userVaultPnl, vaultExpanded, principalPrice, userPosition);

    const stats = [
        {
            value: (
                <StackedCurrencyDisplay
                    alignItems="flex-start"
                    metadata={vaultExpanded?.principalMetadata}
                    amount={borrowStats?.totalDepositsPrincipal ?? 0}
                />
            ),
            caption: COPY.USER_POSITION_TERM
        },
        ...pnlStats
    ];

    const loading =
        userPositionLoading ||
        userPositionFetching ||
        !vaultExpanded ||
        vaultDepositSummaryLoading ||
        vaultDepositSummaryFetching;
    if (!activeWallet) return null;

    return (
        <OutlinedCard>
            <StatRow variant="body1" captionVariant="body2" stats={stats} loading={loading} spacing={2} />
        </OutlinedCard>
    );
}

function useUserVaultDepositStats(): VaultDepositStats | undefined {
    const { vaultExpanded, userPosition: vaultPosition, userPositionLoading } = useVaultContext();

    if (userPositionLoading || !vaultExpanded || !vaultPosition) return undefined;

    const totalDepositsUsd = vaultPosition.totalAmount.amountUsd;

    const totalDepositsPrincipal = vaultPosition.totalAmount.amountPrincipal;

    return {
        totalDepositsPrincipal,
        totalDepositsUsd
    };
}

export function getVaultPnLStats(
    userVaultPnl: UserVaultPnl | undefined,
    vaultExpanded: LendingVaultExpanded | undefined,
    principalPrice: number | undefined,
    userPosition: UserVaultPositionExpanded | undefined
) {
    const isPnlPending = isRecentVaultDepositOrWithdrawal(userVaultPnl?.mostRecentDepositOrWithdrawalTimestamp);

    const noPnl = !userVaultPnl?.vaultPnl;
    const hideTooltip = !userPosition || noPnl || !isPnlPending;

    const stats = [
        {
            value: userVaultPnl?.vaultPnl ? (
                <PnLDisplay
                    vaultExpanded={vaultExpanded}
                    value={userVaultPnl.vaultPnl}
                    principalPrice={principalPrice}
                />
            ) : undefined,
            caption: COPY.INTEREST_EARNED_LABEL,
            hide: !hideTooltip || noPnl
        },
        {
            value: <VaultPendingPnl />,
            caption: COPY.INTEREST_EARNED_LABEL,
            hide: hideTooltip
        }
    ];

    return stats;
}

function PnLDisplay({
    vaultExpanded,
    value,
    principalPrice
}: {
    vaultExpanded: LendingVaultExpanded | undefined;
    value: number;
    principalPrice: number | undefined;
}) {
    if (!principalPrice) return null;

    return (
        <Column alignItems="flex-start" spacing={0.5}>
            <Row spacing={0.5}>
                <ProfitLossText
                    variant="body1"
                    profitLossTokenAmount={value}
                    metadata={vaultExpanded?.principalMetadata}
                />
            </Row>
            <Text variant="body2" color="caption">
                {formatUsd(value * principalPrice)}
            </Text>
        </Column>
    );
}
